<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { ArrowDownTrayIcon, PlusIcon, UserGroupIcon } from '@heroicons/vue/24/outline';
import { PAGE_ICONS } from '@/navigation/navIcons';
import { modelIcon } from '@/composables/modelIcons';
import BrowseSharedViewsDialog from '@/components/BrowseSharedViewsDialog.vue';
import ImportViewDialog from '@/layouts/frame/ImportViewDialog.vue';
import NewSavedViewDialog from '@/layouts/frame/NewSavedViewDialog.vue';
import { useAreaSavedViews } from '@/composables/useAreaSavedViews';
import SavedViewRowMenu from '@/layouts/frame/SavedViewRowMenu.vue';
import SavedViewRowDialogs from '@/layouts/frame/SavedViewRowDialogs.vue';
import { provideSavedViewRowActions } from '@/layouts/frame/savedViewRowActions';
import SavedViewIcon from '@/components/savedViews/SavedViewIcon.vue';
import type { AreaId } from '@/navigation/areas';
import { areaHasSavedViews, listPathFor, viewPathFor, visibleAreas } from '@/navigation/areas';
import RecentItemsList from '@/layouts/frame/RecentItemsList.vue';
import { areaIdForRoute, routeCanSaveView } from '@/navigation/activeArea';
import { isGateOpen } from '@/navigation/gates';
import { reopenOpenView } from '@/composables/useRoutedSavedView';
import OpenSavedViewControls from '@/layouts/frame/OpenSavedViewControls.vue';

/**
 * The column beside the icon strip. It belongs to the area you are in: its title
 * is the area's name, then come the area's record types with their icons, its
 * pages (under a heading only when the area names one, as Music's "Tools"),
 * your saved views, and at the bottom a
 * "Shared views" line for browsing and importing the team's views.
 *
 * Long names are cut off with "…" rather than wrapping, so every row stays one
 * line. The button that hides it is SidebarToggle, at the left of the main
 * area's header, so it stays reachable once the sidebar is hidden.
 */

const route = useRoute();

const area = computed(() => {
    const areaId = areaIdForRoute(route);
    return visibleAreas(isGateOpen).find((candidate) => candidate.id === areaId) ?? null;
});

const recordLinks = computed(() =>
    (area.value?.entities ?? []).map((entity) => ({
        key: entity.entityId,
        label: entity.label,
        icon: modelIcon(entity.entityId),
        to: listPathFor(entity.entityId),
        active: route.meta.entityId === entity.entityId,
    })),
);

const pageLinks = computed(() =>
    (area.value?.pages ?? []).map((page) => ({
        key: page.routeName,
        label: page.label,
        icon: PAGE_ICONS[page.routeName] ?? null,
        to: page.path,
        active: route.name === page.routeName || route.path.startsWith(`${page.path}/`),
    })),
);

const pageGroupLabel = computed(() => area.value?.pagesHeading ?? '');
// Pages with no heading of their own carry on the record types' list, with no gap.
const recordAndPageLinks = computed(() => (pageGroupLabel.value ? recordLinks.value : [...recordLinks.value, ...pageLinks.value]));

/**
 * Saved-view actions only make sense where there is a table to save: an
 * entity's list, or a page with saved views of its own, such as Track matching.
 */
const onSavableTable = computed(() => routeCanSaveView(route));
/**
 * The Saved views and Shared views sections show in an area with lists, or
 * with a page that has saved views. "Import a view" works from anywhere there.
 */
const showsViewSections = computed(() => area.value !== null && areaHasSavedViews(area.value));

const newViewOpen = ref(false);

/** Home's sidebar lists the records and views you opened lately. */
const isHome = computed(() => area.value === null || area.value.id === 'home');

/**
 * Your saved views for this area's lists and pages, such as Track matching's in
 * Music. Opening one puts it in the address.
 */
const { views: areaViews } = useAreaSavedViews(() => (area.value?.id ?? 'home') as AreaId);
const viewLinks = computed(() =>
    areaViews.value.map((view) => ({
        view,
        id: view.id,
        label: view.name,
        to: viewPathFor(view.entity, view.id)!,
        active: route.query?.view === String(view.id),
    })),
);
const browseSharedOpen = ref(false);
const importOpen = ref(false);

// One set of Rename, "Icon and colour" and Delete dialogs for every saved-view
// row, rather than a set inside each row. See savedViewRowActions.
provideSavedViewRowActions();
</script>

<template>
  <div class="flex h-full min-w-0 flex-1 flex-col px-2 pb-2">
    <div class="flex h-12 shrink-0 items-center justify-between pl-2 pr-1">
      <h2 class="truncate text-[15px] font-semibold">{{ area?.label ?? 'Home' }}</h2>
    </div>

    <nav :aria-label="area?.label ?? 'Home'" class="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
      <RecentItemsList v-if="isHome" />

      <section v-if="recordAndPageLinks.length > 0" :aria-label="area?.label" class="flex flex-col gap-0.5">
        <router-link
          v-for="link in recordAndPageLinks"
          :key="link.key"
          :to="link.to"
          :data-testid="recordLinks.includes(link) ? 'section-record-link' : 'section-page-link'"
          :aria-current="link.active ? 'page' : undefined"
          class="flex h-8 min-w-0 items-center gap-2.5 rounded-lg px-2 text-sm text-muted-foreground hover:bg-sidebar-accent hover:text-foreground aria-[current=page]:bg-sidebar-accent aria-[current=page]:text-foreground"
        >
          <component
            :is="link.icon"
            v-if="link.icon"
            class="size-4 shrink-0"
            aria-hidden="true"
            :data-testid="recordLinks.includes(link) ? 'section-record-icon' : 'section-page-icon'"
          />
          <span class="truncate">{{ link.label }}</span>
        </router-link>
      </section>

      <section v-if="pageGroupLabel && pageLinks.length > 0" class="flex flex-col gap-0.5">
        <h3 v-if="pageGroupLabel" class="flex h-7 items-center px-2 text-xs font-medium text-muted-foreground">{{ pageGroupLabel }}</h3>
        <router-link
          v-for="link in pageLinks"
          :key="link.key"
          :to="link.to"
          data-testid="section-page-link"
          :aria-current="link.active ? 'page' : undefined"
          class="flex h-8 min-w-0 items-center gap-2.5 rounded-lg px-2 text-sm text-muted-foreground hover:bg-sidebar-accent hover:text-foreground aria-[current=page]:bg-sidebar-accent aria-[current=page]:text-foreground"
        >
          <component :is="link.icon" v-if="link.icon" class="size-4 shrink-0" aria-hidden="true" data-testid="section-page-icon" />
          <span class="truncate">{{ link.label }}</span>
        </router-link>
      </section>

      <section v-if="showsViewSections" class="flex flex-col gap-0.5" data-testid="section-saved-views">
        <div class="flex h-7 items-center justify-between pl-2 pr-1">
          <h3 class="text-xs font-medium text-muted-foreground">Saved views</h3>
          <button
            v-if="onSavableTable"
            type="button"
            aria-label="New saved view"
            title="New saved view"
            data-testid="saved-views-add"
            class="flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-sidebar-accent hover:text-foreground"
            @click="newViewOpen = true"
          >
            <PlusIcon class="size-3.5" aria-hidden="true" />
          </button>
        </div>
        <div v-for="link in viewLinks" :key="link.id" class="group flex min-w-0 items-center rounded-lg pr-1 hover:bg-sidebar-accent has-[[aria-current=page]]:bg-sidebar-accent">
          <router-link
            :to="link.to"
            data-testid="section-view-link"
            :aria-current="link.active ? 'page' : undefined"
            @click="link.active && reopenOpenView()"
            class="flex h-8 min-w-0 flex-1 items-center gap-2.5 px-2 text-sm text-muted-foreground hover:text-foreground aria-[current=page]:text-foreground"
          >
            <!-- The view's own icon and colour, as in recent items, the breadcrumb and Cmd+K. -->
            <SavedViewIcon :appearance="link.view" class="size-4" data-testid="section-view-icon" />
            <span class="truncate">{{ link.label }}</span>
          </router-link>
          <OpenSavedViewControls v-if="link.active && onSavableTable" :view="link.view" />
          <SavedViewRowMenu v-else :view="link.view" />
        </div>
        <p v-if="viewLinks.length === 0" class="px-2 py-1 text-xs text-muted-foreground">No saved views yet.</p>
      </section>

      <slot />

      <!-- At the bottom: one heading line, with Browse and Import as its buttons, like
           the "+" on Saved views. Shared views are found through Browse, not listed. -->
      <section v-if="showsViewSections" class="mt-auto flex flex-col gap-0.5" data-testid="section-shared-views">
        <div class="flex h-7 items-center justify-between pl-2 pr-1">
          <h3 class="text-xs font-medium text-muted-foreground">Shared views</h3>
          <div class="flex items-center gap-0.5">
            <button
              v-if="onSavableTable"
              type="button"
              aria-label="Browse shared views"
              title="Browse shared views"
              data-testid="sidebar-browse-shared"
              class="flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-sidebar-accent hover:text-foreground"
              @click="browseSharedOpen = true"
            >
              <UserGroupIcon class="size-3.5" aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label="Import a view"
              title="Import a view"
              data-testid="sidebar-import-view"
              class="flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-sidebar-accent hover:text-foreground"
              @click="importOpen = true"
            >
              <ArrowDownTrayIcon class="size-3.5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>
    </nav>

    <NewSavedViewDialog v-if="onSavableTable" v-model:open="newViewOpen" />
    <BrowseSharedViewsDialog v-if="onSavableTable" v-model:open="browseSharedOpen" />
    <ImportViewDialog v-model:open="importOpen" />
    <SavedViewRowDialogs :views="areaViews" />
  </div>
</template>
